import React, {
    FormEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { Loading } from "../..";
import Confirm from "../../confirm/Confirm";
import Agents from "../../pickers/agents/Agents";
import {
    GetUserGroupDocument,
    GetUserGroupsDocument,
    ListUserGroupsDocument,
    UserGroup,
    useDeleteUserGroupMutation, useGetUserGroupLazyQuery, useSaveUserGroupMutation,
} from "../../../__generated__/api";
import useDirtyTracker from "../../dirty-tracker/DirtyTracker";
import PageActions from "../../page-actions/PageActions";

export interface DisplayUserGroupsProps {
    id?: string;
    canDelete?: boolean;
    isModal?: boolean;
    onSave: (id: Partial<UserGroup>) => void;
    onDeleted?: (id: string) => void;
    onHide?: () => void;
    className?: string;
}
export default function DisplayUserGroup({
    id, canDelete, onSave, isModal, onHide, className, onDeleted,
}: DisplayUserGroupsProps): ReactElement {
    const [loadQuery, { data, loading: isLoading }] = useGetUserGroupLazyQuery({
        fetchPolicy: "no-cache",
        variables: {
            id,
        },
    });

    const [deleteUserGroup] = useDeleteUserGroupMutation();

    const [save] = useSaveUserGroupMutation();

    const [name, setName] = useState(() => data?.userGroup?.name ?? "");
    const [description, setDescription] = useState(() => data?.userGroup?.description ?? "");
    const [userIds, setUserIds] = useState<string[]>(() => data?.userGroup?.userIds ?? []);

    const onSubmit = (event?: FormEvent, onDone?: () => void) => {
        event?.preventDefault();
        (async () => {
            const userGroup = {
                name,
                description,
                userIds,
            };
            const saved = await save({
                refetchQueries: [
                    { query: ListUserGroupsDocument },
                    { query: GetUserGroupsDocument },
                    { query: GetUserGroupDocument },
                ],
                variables: {
                    id: id !== "new" ? id : undefined,
                    userGroup,
                },
            });
            onDone?.();
            onSave({
                ...userGroup,
                id: saved.data?.saveUserGroup.id as string,
            });
        })();
    };

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "User group saved.",
        onSave: (event, onDone) => {
            onSubmit(event, onDone);
        },
    });

    dirtyTracker.useWatchProperty(name, data?.userGroup?.name ?? "");
    dirtyTracker.useWatchProperty(description, data?.userGroup?.description ?? "");
    dirtyTracker.useWatchDeepCompareProperty(userIds ?? [], data?.userGroup?.userIds ?? []);

    dirtyTracker.useLoad(() => {
        setName(data?.userGroup?.name ?? "");
        setDescription(data?.userGroup?.description ?? "");
        setUserIds(data?.userGroup?.userIds ?? []);
    }, [data?.userGroup]);

    if (isLoading) {
        return <Loading />;
    }

    if (id && id !== "new" && !data?.userGroup) {
        loadQuery({
            variables: {
                id,
            },
        });
    }

    const onDelete = async () => {
        await deleteUserGroup({
            refetchQueries: [
                { query: ListUserGroupsDocument },
                { query: GetUserGroupsDocument },
                { query: GetUserGroupDocument },
            ],
            variables: {
                id,
            },
        });
        onDeleted?.(id as string);
    };

    const form = (
        <>

            <div className={className}>
                <h5 className="mb-3 text-light">
                    {!id ? "Create" : ""}
                    {" "}
                    Group
                </h5>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Name</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="My Group"
                        required
                        onChange={evt => setName(evt.target.value)}
                        value={name as string}
                    />
                    <Form.Text>
                        The name of the Group.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">
                        Description
                        {" "}
                        <small className="text-light">(Optional)</small>
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="This group is used for .."
                        as="textarea"
                        onChange={evt => setDescription(evt.target.value)}
                        value={description as string}
                    />
                    <Form.Text>
                        Any information you want to leave to help you understand what this group is created for.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Users</Form.Label>
                    <Agents placeholder="Please pick an agent" isMulti onSelected={ids => setUserIds(ids)} value={userIds} />
                    <Form.Text>
                        Who are the users of this group
                    </Form.Text>
                </Form.Group>

            </div>

            {!isModal && (
                <>
                    {id !== "new" && canDelete && (
                        <PageActions
                            className="mt-4"
                            onDelete={onDelete}
                            deleteConfirm={(
                                <div>
                                    Are you sure you want to delete the group:
                                    {" "}
                                    <div className="mt-2 p-2 rounded alert alert-danger">
                                        {name}
                                    </div>

                                </div>
                            )}
                        />
                    )}
                    {dirtyTracker.saveComponent}
                </>
            ) }
        </>
    );

    return isModal ? (
        <Confirm
            shouldShow
            confirmText="Create"
            onHide={() => onHide?.()}
            onConfirm={() => onSubmit()}
        >
            {form}
        </Confirm>
    ) : (
        <form className="flex-grow-1" onSubmit={onSubmit}>
            {form}
        </form>
    );
}
DisplayUserGroup.defaultProps = {
    canDelete: false,
    id: undefined,
    isModal: false,
    onHide: undefined,
    className: undefined,
    onDeleted: undefined,
};
